import { Disclosure } from "@headlessui/react"
import { HiChevronRight, HiCheckCircle } from "react-icons/hi"
import { useLocation } from "react-router-dom"
import { useState, useRef, useEffect } from "react"
import { useQuery } from "react-query"
import {
  getTutorAcademyDetail,
  getTutorAcademyProgressSummary,
  upsertTutorVideoProgress,
  downloadTutorCertificate
} from "../../../API/API"

import Spinner from "../../../components/Spinner"
import Error from "../../../components/Status/Error"

import { api } from "../../../util/APIConfig"
import { BeatLoader } from "react-spinners"
import { toast } from "react-toastify"

import ReactPlayer from "react-player"

import { BsPlayCircle } from "react-icons/bs"

export default function TutorAcademyCourse() {
  const { state, pathname } = useLocation()

  const [url, setUrl] = useState("")
  const [videoId, setVideoId] = useState("")
  const [questions, setQuestions] = useState([])
  const [videoTitle, setVideoTitle] = useState("")
  const [completed, setCompleted] = useState(false)
  const [isChecked, setIsChecked] = useState(false)
  const [mandatory, setMandatory] = useState(false)
  const [questionPage, setQuestionPage] = useState(1)
  const [answersStatus, setAnswersStatus] = useState([])
  const [isAnswering, setIsAnswering] = useState(false)
  const [videoQuestions, setVideoQuestions] = useState([])
  const [selectedAnswer, setSelectedAnswer] = useState("")
  const [selectedQuestionId, setSelectedQuestionId] = useState("")

  const [failed, setFailed] = useState(false)

  const playerRef = useRef(null)
  const lastSentRef = useRef(0)
  const [resumeSeconds, setResumeSeconds] = useState(0)
  const [playing, setPlaying] = useState(false)
  const PROGRESS_PUT_INTERVAL_MS = 10000

  useEffect(() => {
    setPlaying(false)
  }, [videoId])

  const {
    data: summaryData,
    refetch: refetchSummary,
    isLoading: isSummaryLoading
  } = useQuery(
    ["academy-progress", state.id],
    async () => {
      const res = await getTutorAcademyProgressSummary(state.id)
      return res.data.data
    },
    { enabled: !!state?.id }
  )

  const completedVideoIds = summaryData?.completedVideoIds ?? []
  const completedVideos = summaryData?.completedVideos ?? 0

  useEffect(() => {
    if (!videoId) return
    const p = summaryData?.progress?.find(x => x.videoId === videoId)
    setResumeSeconds(p?.lastPositionSeconds ?? 0)
  }, [videoId, summaryData])

  async function sendProgress(positionSeconds, percent) {
    if (!videoId) return
    try {
      const res = await upsertTutorVideoProgress({
        videoId,
        academyId: state.id,
        positionSeconds: Math.floor(positionSeconds),
        percent
      })
      const p = res?.data?.data
      if (p?.isCompleted && !completedVideoIds.includes(videoId)) {
        refetchSummary()
      }
    } catch (err) {
      console.log(err)
    }
  }

  function handlePlayerReady() {
    const player = playerRef.current
    if (!player || resumeSeconds <= 0) return
    const duration = player.getDuration() || 0
    if (duration > 0 && resumeSeconds >= duration - 5) return
    player.seekTo(resumeSeconds, "seconds")
    setPlaying(true)
  }

  function handlePlayerPlay() {
    setPlaying(true)
  }

  function handlePlayerProgress({ playedSeconds, played }) {
    const now = Date.now()
    if (now - lastSentRef.current < PROGRESS_PUT_INTERVAL_MS) return
    lastSentRef.current = now
    sendProgress(playedSeconds, played)
  }

  function handlePlayerPause() {
    setPlaying(false)
    const player = playerRef.current
    if (!player) return
    const playedSeconds = player.getCurrentTime() ?? 0
    const duration = player.getDuration() || 0
    const played = duration > 0 ? playedSeconds / duration : 0
    lastSentRef.current = Date.now()
    sendProgress(playedSeconds, played)
  }

  function handlePlayerEnded() {
    const player = playerRef.current
    const duration = player?.getDuration() || 0
    lastSentRef.current = Date.now()
    sendProgress(duration, 1)
  }

  async function handleDownloadCertificate() {
    try {
      const res = await downloadTutorCertificate(state.id)
      const blob = res.data
      const objectUrl = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = objectUrl
      a.download = `${(data?.data?.data?.name ?? "Certificate").replace(/\s+/g, "-")}-Certificate.png`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(objectUrl)
    } catch (err) {
      toast.error("Could not download certificate")
    }
  }

  function handleMCQAnswer(index, answer) {
    const newAnswersStatus = [...answersStatus]
    newAnswersStatus[index] = answer
    setAnswersStatus(newAnswersStatus)
  }

  async function getQuestions(videoQuestion) {
    const questions = await Promise.all(
      videoQuestion.map(async question => {
        const response = await api.post("question/QuestionById", {
          id: question.questionId
        })
        return response.data
      })
    )
    setQuestions(questions)
  }

  const { data, status, refetch } = useQuery(
    ["videos", pathname],
    () => {
      return getTutorAcademyDetail(state.id)
    },
    {
      onSuccess: data => {
        if (!videoId) {
          setVideoId(data.data.data.videoGroups[0].videos[0].id)
          setIsChecked(data.data.data.videoGroups[0].videos[0].isCompleted)
          setUrl(data.data.data.videoGroups[0].videos[0].url)
          setVideoTitle(data.data.data.videoGroups[0].videos[0].name)
          setVideoQuestions(
            data.data.data.videoGroups[0].videos[0].videoQuestion
          )
          setMandatory(
            data.data.data.videoGroups[0].videos[0].mandatoryQuestion
          )
          data.data.data.videoGroups[0].videos[0].videoQuestion.length > 0 &&
            getQuestions(data.data.data.videoGroups[0].videos[0].videoQuestion)
        }
      }
    }
  )

  async function handleAnswer() {
    setIsAnswering(true)
    try {
      await api.post("/academy/api/VideoGroup/UserVideoQuestionCreate", {
        videoQuestionId: videoQuestions[selectedQuestionId].id,
        optionSelected: selectedAnswer
      })
      if (questionPage === questions.length) {
        if (answersStatus.includes(false)) {
          toast.error(
            "Some of the answers are wrong, please review the video and try again"
          )
          setFailed(true)
          setCompleted(false)
          return setQuestionPage(1)
        }
        setQuestionPage(1)
        setFailed(false)
        return setCompleted(false)
      }
      setQuestionPage(prev => {
        if (prev === questions.length) return prev
        return prev + 1
      })
    } catch (err) {
      console.log(err)
    } finally {
      setIsAnswering(false)
    }
  }

  if (status === "loading") {
    return <Spinner />
  }

  if (status === "error") {
    return <Error />
  }

  if (status === "success") {
    return (
      <main className="container-in">
        <div className="flex w-full flex-col gap-7 md:flex-row">
          <div className="w-full rounded-xl bg-white lg:w-[35%]">
            <div className="flex w-full flex-wrap items-center justify-between rounded-t-xl bg-primary px-6 py-6">
              <h3 className="heading-4l font-semibold text-white">Contents</h3>
              <div className="flex flex-col gap-2">
                <div className="h-1 w-full rounded-lg bg-blue-2">
                  <div
                    className={`h-1 rounded-lg bg-white`}
                    style={{
                      width: `${
                        data.data.data.totalVideos
                          ? (completedVideos / data.data.data.totalVideos) * 100
                          : 0
                      }%`
                    }}
                  />
                </div>
              </div>
            </div>
            {data.data.data.totalVideos > 0 &&
              completedVideos >= data.data.data.totalVideos &&
              /^Tutor Training Module \d+$/i.test(data.data.data.name ?? "") && (
                <div className="flex justify-center px-6 py-4">
                  <button
                    onClick={handleDownloadCertificate}
                    className="rounded-full bg-secondary-green px-6 py-2 font-semibold text-white hover:opacity-90"
                  >
                    Download Certificate
                  </button>
                </div>
              )}
            <div className="w-full px-5 md:px-9">
              <div className="w-full rounded-2xl bg-white py-6 lg:max-w-md ">
                {data.data.data.videoGroups.map((group, index) => {
                  return (
                    <Disclosure
                      key={index}
                      defaultOpen={index === 0 ? true : false}
                    >
                      {({ open }) => (
                        <>
                          <Disclosure.Button className="flex w-full items-center justify-between">
                            <span className="heading-4 font-semibold text-neutral-1">
                              {group.name}
                            </span>
                            <HiChevronRight
                              size={24}
                              className={`transition-all duration-300 ${
                                open ? "rotate-90 transform " : ""
                              }`}
                            />
                          </Disclosure.Button>

                          <Disclosure.Panel className="curriculum-scroll max-h-[40vh]">
                            <ul className="mr-5">
                              {group.videos.map((video, index) => {
                                return (
                                  <li
                                    key={index}
                                    className={`py-3 ${
                                      (completed && mandatory) ||
                                      (failed && mandatory)
                                        ? "cursor-not-allowed"
                                        : "cursor-pointer"
                                    }`}
                                  >
                                    <div className="flex justify-between">
                                      <div className="flex items-center gap-5">
                                        {completedVideoIds.includes(video.id) ? (
                                          <HiCheckCircle
                                            size={20}
                                            className="shrink-0 text-secondary-green"
                                          />
                                        ) : (
                                          <span className="inline-block h-[20px] w-[20px] shrink-0 rounded-full border-2 border-neutral-9/30" />
                                        )}
                                        <span
                                          className={`heading-5 font-medium ${
                                            video.name === videoTitle
                                              ? "text-primary"
                                              : "text-neutral-9"
                                          }`}
                                          onClick={() => {
                                            if (completed && mandatory) return
                                            if (failed && mandatory) return
                                            setCompleted(false)
                                            setUrl(video.url)
                                            setVideoTitle(video.name)
                                            setVideoId(video.id)
                                            setVideoQuestions(
                                              video.videoQuestion
                                            )
                                            setMandatory(
                                              video.mandatoryQuestion
                                            )
                                            setIsChecked(video.isCompleted)
                                            video.videoQuestion.length > 0
                                              ? getQuestions(
                                                  video.videoQuestion
                                                )
                                              : setQuestions([])
                                          }}
                                        >
                                          {video.name}
                                        </span>
                                      </div>
                                    </div>
                                  </li>
                                )
                              })}
                            </ul>
                          </Disclosure.Panel>
                          <div className="my-5 h-[1px] w-full bg-border-line" />
                        </>
                      )}
                    </Disclosure>
                  )
                })}
              </div>
            </div>
          </div>

          {url && videoTitle && !isSummaryLoading && (
            <div className="relative w-full rounded-[18px] bg-white md:h-[500px] lg:w-[65%]">
              {completed && questions.length > 0 && (
                <div className="absolute z-50 h-full w-full rounded-lg bg-white shadow-lg">
                  <div className="flex items-center justify-between rounded-t-[18px] bg-primary py-4 text-white">
                    <p className="px-10 py-3">Question</p>
                  </div>
                  <hr />
                  {questions.map(
                    (question, index) =>
                      questionPage === index + 1 && (
                        <div key={question.id}>
                          <div className="px-10 py-5">
                            <div
                              dangerouslySetInnerHTML={{
                                __html: question.text
                              }}
                              className="nav-link pb-5"
                            />
                            <div className="flex flex-col gap-4">
                              {question.answers.map(answer => (
                                <div
                                  key={answer.id}
                                  className="flex items-center gap-3"
                                >
                                  <input
                                    type="radio"
                                    name={question.id}
                                    id={answer.id}
                                    value={answer.id}
                                    className="cursor-pointer accent-primary"
                                    onChange={() => {
                                      setSelectedAnswer(answer.letter)
                                      setSelectedQuestionId(index)
                                      handleMCQAnswer(index, answer.isCorrect)
                                    }}
                                  />
                                  <label
                                    htmlFor={answer.id}
                                    className="nav-link cursor-pointer"
                                  >
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: answer.text
                                      }}
                                    />
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>

                          <hr />
                          <div className="flex items-center justify-end gap-7 px-10 py-3">
                            <button
                              className="hidden font-semibold text-primary"
                              onClick={() =>
                                setQuestionPage(prev => {
                                  if (prev === questions.length) return prev
                                  return prev + 1
                                })
                              }
                            >
                              Skip
                            </button>
                            <button
                              onClick={handleAnswer}
                              className="rounded-full bg-primary px-3 py-1 text-white"
                            >
                              {isAnswering ? (
                                <BeatLoader color="#fff" size={10} />
                              ) : questionPage === questions.length ? (
                                "Submit"
                              ) : (
                                "Next"
                              )}
                            </button>
                          </div>
                        </div>
                      )
                  )}
                </div>
              )}
              <div className="flex items-center justify-between rounded-t-[18px] bg-secondary-green px-8 py-6">
                <h4 className="heading-4l font-semibold text-white">
                  {videoTitle}
                </h4>
                {/* <input
                  type="checkbox"
                  className="checkbox-white checkbox h-[18px] w-[18px] rounded-[2px] border-[2px] border-white"
                  checked={isChecked}
                  readOnly
                /> */}
              </div>
              <div className="relative w-full px-5 py-10 md:px-8">
                <ReactPlayer
                  ref={playerRef}
                  width="100%"
                  url={url}
                  controls={true}
                  playing={playing}
                  playIcon={<BsPlayCircle size={100} color="white" />}
                  progressInterval={3000}
                  onReady={handlePlayerReady}
                  onProgress={handlePlayerProgress}
                  onPlay={handlePlayerPlay}
                  onPause={handlePlayerPause}
                  onEnded={handlePlayerEnded}
                />
              </div>
            </div>
          )}
        </div>
      </main>
    )
  }
}
